import { http, setToken } from './client'

/**
 * 손님이 자기 예약을 보는 통로.
 *
 * <p><b>예약번호를 보내지 않습니다.</b> 본인 확인 때 받은 열쇠 안에 들어 있고,
 * 서버가 거기서 꺼내 씁니다. 그래서 남의 번호를 적어 보낼 자리가 없습니다 —
 * 레거시는 이것이 없어 번호만 바꾸면 남의 개인정보가 나왔습니다.</p>
 */

/** 예약 안내 머리말. */
export interface ReservationHead {
  rsvNo: string | null
  rsvNm: string | null
  telNo: string | null
  corpNm: string | null
  /** 예약·계약·마감 중 지금 어디쯤인지. 사람 말로 옵니다. */
  rsvRes: string | null
  carNm: string | null
  carFuel: string | null
  carYearType: string | null
  carYearModel: string | null
  toDatetime: string | null
  rtDatetime: string | null
  /** 빌리는 날수. */
  diffDatetime: number | null
  toPlace: string | null
  insureNm: string | null
  insureLimit: number | null
  /** 자기부담금 제외 항목. */
  nonItem: string | null
  payName: string | null
  rentAmt: number | null
  insureAmt: number | null
  serviceAmt: number | null
  totalSum: number | null
  /** 아직 안 낸 금액. */
  balAmt: number | null
}

/** 예약에 붙은 부가서비스 한 줄. */
export interface AddService {
  serviceNm: string | null
  cnt: number | null
  serviceAmt: number | null
  serviceTotalAmt: number | null
  /** 개수 × 단가로 다시 센 값. 저장된 합계와 다를 수 있습니다(레거시 D13). */
  checkAmt: number | null
}

export interface ReservationDocument {
  head: ReservationHead | null
  addServices: AddService[]
}

/** 운전자 한 사람. 계약 안내에서 보여 줍니다. */
export interface Driver {
  driverNm: string | null
  birthDay: string | null
  licenceNo: string | null
  licenceType: string | null
  driverGb: string | null
}

/**
 * 계약·마감 안내 머리말 — <b>실제로 어떻게 이용했는지</b>와 정산.
 *
 * <p>차를 받기 전에는 대부분 비어 있습니다. 받고 나서 채워지고,
 * 돌려주면 마지막 금액이 잡힙니다.</p>
 */
export interface TripHead {
  rsvNo: string | null
  carNm: string | null
  carNo: string | null
  carFuel: string | null
  /** 지금 어디쯤인지. '예약' · '계약' · '마감' 같은 말로 옵니다. */
  rsvStateNm: string | null

  /** 약속한 인수·반납. */
  toDatetime: string | null
  rtDatetime: string | null
  /** 약속한 이용 시간(시). */
  rmHour: number | null

  /** <b>실제로</b> 돌려준 시각. 아직 안 돌려줬으면 비어 있습니다. */
  actEndDatetime: string | null
  /** <b>실제로</b> 이용한 시간(시). */
  raHour: number | null

  strFuel: number | null
  rtFuel: number | null

  insureNm: string | null
  insureLimit: number | null
  nonItem: string | null

  rentAmt: number | null
  insureAmt: number | null
  /** 부가서비스 합계. */
  rasAmt: number | null
  /** 연장 요금. */
  ovAmt: number | null
  /** 연료비. */
  fuelAmt: number | null
  /** 합계. 서버가 글자로 줍니다. */
  sumAmt: string | null
  /** 아직 안 낸 금액. */
  balAmt: number | null
}

/** 계약·마감 안내. 차를 받은 뒤부터 볼 것이 생깁니다. */
export interface ContractDocument {
  head: TripHead | null
  drivers: Driver[]
}

/** 본인 확인 결과. */
export interface VerifyResult {
  accessToken: string
  expiresAt: string
  rsvNo: string
  guestName: string | null
}

export const reservationApi = {
  /**
   * <b>본인 확인</b> — 예약번호와 전화번호 뒷 네 자리.
   *
   * <p>통과하면 그 예약 하나에만 묶인 열쇠를 받아 담아 둡니다.
   * 틀린 이유는 서버가 알려 주지 않습니다 — "번호는 맞는데 전화가 틀렸다" 고
   * 알려 주면 번호를 훑어 찾는 데 쓰입니다.</p>
   */
  async verify(rsvNo: string, telLast4: string): Promise<VerifyResult> {
    const { data } = await http.post<VerifyResult>(
      '/customer/verify', { rsvNo, telLast4 }, { quiet: true } as never)
    setToken(data.accessToken)
    return data
  },

  /** 예약 안내 — 차량·일정·보험·요금. */
  reservation: (s?: AbortSignal) =>
    http.get<ReservationDocument>('/customer/reservation', { signal: s }).then((r) => r.data),

  /** 계약·마감 안내 — 실제로 쓴 연료·거리와 정산. */
  contract: (s?: AbortSignal) =>
    http.get<ContractDocument>('/customer/contract', { signal: s }).then((r) => r.data),
}
